import { useEffect, useState } from 'react';
import { useHandleCharacterFetchClick } from '@components'
import { Character, User } from '@shared-types'
import { Utils } from '@client/utility';
import { useHomeStore } from '@client/store';

interface UseUserCreatedCharactersResponse {
  userData: User | null;
  currentPage: number;
  maxPage: number;
  totalItems: number;
  characters: Character[];
  loading: boolean;
  handleButtonClick: (
    type: 'hot' | 'latest' | 'random' | 'tags',
    maxCharacter?: number,
    page?: number,
    tag?: string,
    gender?: string
  ) => Promise<void>;
  handleButtonTagClicked: (tag: string) => void;
  goToNextPage: () => void;
  goToPrevPage: () => void;
}

export function getUserCreatedCharacters(
  creatorUUID: string | null,
  perPage: number = 20,
  searchQuery: string = ''
): UseUserCreatedCharactersResponse {
  const {
    characters,
    setCharacters,
    loading,
    setLoading,
    currentPage,
    setCurrentPage,
    maxPage,
    setMaxPage,
    sortType,
    setSortType,
    tag,
    setTag,
    gender,
    setGender,
  } = useHomeStore();

  const [userData, setUserData] = useState<User | null>(null);
  const [userLoading, setUserLoading] = useState(!creatorUUID);
  const [totalItems, setTotalItems] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [includeSensitive, setIncludeSensitive] = useState(false);

  const fetchCharacterData = useHandleCharacterFetchClick();

  const goToNextPage = () => {
    if (currentPage < maxPage) setCurrentPage(currentPage + 1);
  };

  const goToPrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const fetchCharacters = async (): Promise<void> => {
    if (!creatorUUID) {
      setCharacters([]);
      return;
    }
    setLoading(true);
    try {
      const result = await fetchCharacterData(
        sortType,
        perPage,
        currentPage,
        creatorUUID,
        tag,
        gender,
        searchQuery,
        includeSensitive
      );
      setCharacters(result.characters);
      setMaxPage(result.totalPages > 0 ? result.totalPages : 1);
      setTotalItems(result.totalItems ?? result.characters.length);
      setLoaded(true);
    } catch {
      setCharacters([]);
      setMaxPage(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const handleButtonClick = async (
    type: 'hot' | 'latest' | 'random' | 'tags',
    maxCharacter: number = perPage,
    page: number = 1,
    tagVal?: string,
    genderVal?: string
  ): Promise<void> => {
    setSortType(type);
    setCurrentPage(page);
    if (tagVal) setTag(tagVal);
    if (genderVal) setGender(genderVal as 'male' | 'female');
    await fetchCharacters();
  };

  const handleButtonTagClicked = (tagVal: string) => {
    setTag(tagVal);
    setSortType('tags');
    setCurrentPage(1);
  };

  useEffect(() => {
    if (creatorUUID && !loaded) fetchCharacters();
  }, [creatorUUID, loaded, includeSensitive]);

  useEffect(() => {
    if (creatorUUID && loaded) fetchCharacters();
  }, [currentPage, sortType, tag, gender, includeSensitive]);

  useEffect(() => {
    if (!creatorUUID) {
      setCharacters([]);
      setLoaded(false);
    }
  }, [creatorUUID]);

  useEffect(() => {
    if (!creatorUUID) {
      setUserLoading(true);
      return;
    }
    const fetchUserData = async (uuid: string): Promise<User | null> => {
      try {
        const userRes = await Utils.db.select<{
          username?: string;
          avatar_url?: string;
          user_uuid?: string;
          include_sensitive?: boolean;
        }>({
          tables: 'user_data',
          columns: 'username, avatar_url, user_uuid, include_sensitive',
          match: { user_uuid: uuid },
          paging: false,
          countOption: null,
        });

        const user = userRes.data?.[0];
        if (!user) return null;
        
        setIncludeSensitive(user.include_sensitive ?? false);
        
        return {
          username: user.username ?? '',
          user_avatar: user.avatar_url ?? '',
          user_uuid: user.user_uuid ?? '',
        };
      } catch {
        return null;
      }
    };
    (async () => {
      setUserLoading(true);
      const user = await fetchUserData(creatorUUID);
      setUserData(user);
      setUserLoading(false);
    })();
  }, [creatorUUID]);

  return {
    userData,
    currentPage,
    maxPage,
    totalItems,
    characters,
    loading: userLoading || loading || (!loaded && !!creatorUUID),
    handleButtonClick,
    handleButtonTagClicked,
    goToNextPage,
    goToPrevPage,
  };
}